import { productIds } from '@eti/products';
import { useTranslation } from '@eti/providers';
import { css, cx, etiColors } from '@eti/styles';
import { LinkButton } from '@etg/wings';
import { ScreenReaderPrice } from '@eti/components';
import type { ReactNode } from 'react';
import { CartRow, CartSubSectionTitle, Price } from '../CartComponents';
import { useToggle } from '../../../../common/custom-hooks/toggle';
import useNameFormatter from '../../../../common/custom-hooks/useNameFormatter';
import CartProductOverlay from './CartProductOverlay';

const productInfoStyles = css`
  display: flex;
  flex-direction: column;
  position: relative;
`;

const productTextStyles = css`
  white-space: pre-wrap;
`;

const readMoreWrapperStyles = css`
  margin-top: 6px;
`;

const travelerNamesWrapperStyles = css`
  color: ${etiColors.grayScale[10]};
  margin-top: 6px;
  padding-inline-start: 32px;

  & > div {
    display: list-item;
  }
`;

interface TravelerNamesProps {
  children: ReactNode;
  className?: string;
}

export const TravelerNames = ({ children, className }: TravelerNamesProps) => (
  <div className={cx(travelerNamesWrapperStyles, className)}>{children}</div>
);

const travelerNameStyles = css`
  font-size: 0.75rem;
`;

interface CartProductProps {
  id: string;
  price: string;
  text: string;
  title: string;
  travelers: {
    firstName: string;
    lastName: string;
  }[];
}

const CartProduct = ({ id, price, text, title, travelers = [] }: CartProductProps) => {
  const { t } = useTranslation();
  const {
    isOpen: isProductOverlayOpen,
    handleOpen: handleOpenProductOverlay,
    handleClose: handleCloseProductOverlay,
  } = useToggle(false);
  const { formatName } = useNameFormatter();

  const showReadMoreButton = text?.length > 170;

  return (
    <>
      <CartSubSectionTitle data-testid="cart-product-title">
        <CartRow>
          <span data-testid={`cart-product-${productIds[Number(id)]}-title`}>{title}</span>
          <Price aria-hidden data-testid={`cart-product-${productIds[Number(id)]}-price`}>
            {price}
          </Price>
          <ScreenReaderPrice price={price} />
        </CartRow>
      </CartSubSectionTitle>
      {text && (
        <>
          <div
            className={productInfoStyles}
            data-testid={`cart-product-${productIds[Number(id)]}-info`}
          >
            <div className={productTextStyles}>
              {showReadMoreButton ? `${text.substring(0, 170).trimEnd()}...` : text}
              {showReadMoreButton && (
                <div className={readMoreWrapperStyles}>
                  <LinkButton
                    data-testid={`cart-product-${productIds[Number(id)]}-readMore-button`}
                    onClick={handleOpenProductOverlay}
                  >
                    {t('Products.ReadMore.Link.Label')}
                  </LinkButton>
                </div>
              )}
            </div>
          </div>
          <CartProductOverlay
            isOpen={isProductOverlayOpen}
            onClose={handleCloseProductOverlay}
            text={text}
            title={title}
            travelers={travelers}
          />
        </>
      )}
      {travelers?.length > 0 && !showReadMoreButton && (
        <TravelerNames data-testid={`cart-product-${productIds[Number(id)]}-travelers`}>
          {travelers.map(({ firstName, lastName }) => (
            <div className={travelerNameStyles} key={`${firstName}-${lastName}`}>
              {formatName(firstName, lastName)}
            </div>
          ))}
        </TravelerNames>
      )}
    </>
  );
};

export default CartProduct;
